// src/pages/Settings/WithdrawalHistory.jsx
import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import API from '../../services/api';

const WithdrawalHistory = () => {
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending, approved, rejected, cancelled
  const [summary, setSummary] = useState({
    totalWithdrawn: 0,
    pendingCount: 0,
    approvedCount: 0,
    rejectedCount: 0,
    totalRequests: 0
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  useEffect(() => {
    fetchWithdrawalHistory();
  }, [filter, pagination.page]);

  const fetchWithdrawalHistory = async () => {
    try {
      setLoading(true);
      const response = await API.get('/admin/withdraw/history', {
        params: {
          status: filter !== 'all' ? filter : undefined,
          page: pagination.page,
          limit: pagination.limit
        }
      });

      if (response.data.success) {
        setWithdrawals(response.data.withdrawals || []);
        setSummary(response.data.summary);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination.total,
          pages: response.data.pagination.pages
        }));
      }
    } catch (error) {
      console.error('Error fetching withdrawal history:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.error || 'Failed to load withdrawal history',
        confirmButtonColor: '#f7a90b'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelWithdrawal = async (withdrawalId) => {
    const result = await Swal.fire({
      title: 'Cancel Withdrawal?',
      text: 'Are you sure you want to cancel this withdrawal request? The amount will be refunded to your wallet.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f7a90b',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, cancel it!'
    });

    if (result.isConfirmed) {
      try {
        const response = await API.delete(`/admin/withdraw/${withdrawalId}/cancel`);
        
        if (response.data.success) {
          Swal.fire({
            icon: 'success',
            title: 'Cancelled!',
            text: 'Withdrawal request has been cancelled and amount refunded.',
            confirmButtonColor: '#f7a90b'
          });
          
          // Refresh the list
          fetchWithdrawalHistory();
        }
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.response?.data?.error || 'Failed to cancel withdrawal',
          confirmButtonColor: '#f7a90b'
        });
      }
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { class: 'badge badge-pending', text: 'Pending' },
      approved: { class: 'badge badge-active', text: 'Approved' },
      rejected: { class: 'badge badge-inactive', text: 'Rejected' },
      cancelled: { class: 'badge badge-secondary', text: 'Cancelled' }
    };
    const config = statusConfig[status] || statusConfig.pending;
    return <span className={config.class}>{config.text}</span>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '--';
    return new Date(dateString).toLocaleString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatAmount = (amount) => {
    return `${amount.toLocaleString()} Points`;
  };

  const filteredWithdrawals = withdrawals; // Filter is handled by API now

  if (loading) {
    return (
      <div className="main-page">
        <div className="container-fluid">
          <div className="text-center py-5">
            <div className="spinner-border text-warning" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-page">
      <div className="container-fluid">
        <div className="page-hedding-header">
          <h1 className="page-heading h3 text-uppercase fs-4">Withdrawal History</h1>
        </div>

        {/* Summary Cards */}
        <div className="row mb-4">
          <div className="col-md-3 mb-3">
            <div className="stat-card purple">
              <p>Total Withdrawn</p>
              <h3>{formatAmount(summary.totalWithdrawn)}</h3>
            </div>
          </div>
          <div className="col-md-3 mb-3">
            <div className="stat-card orange">
              <p>Pending Requests</p>
              <h3>{summary.pendingCount}</h3>
            </div>
          </div>
          <div className="col-md-3 mb-3">
            <div className="stat-card green">
              <p>Approved</p>
              <h3>{summary.approvedCount}</h3>
            </div>
          </div>
          <div className="col-md-3 mb-3">
            <div className="stat-card red">
              <p>Rejected</p>
              <h3>{summary.rejectedCount}</h3>
            </div>
          </div>
        </div>

        <div className="admin-card-one">
          <div className="admin-card-header">
            <h2 className="admin-card-heading">Withdrawal Requests</h2>
            <p className="text-muted mt-1 mb-0" style={{ color: '#fff7ca !important', fontSize: '14px' }}>
              View all your withdrawal requests and their status
            </p>
          </div>
          <div className="admin-card-body">
            {/* Filter Buttons */}
            <div className="mb-4">
              <div className="btn-group" role="group">
                <button 
                  className={`btn ${filter === 'all' ? 'btn-warning' : 'btn-outline-warning'}`}
                  onClick={() => setFilter('all')}
                >
                  All ({summary.totalRequests})
                </button>
                <button 
                  className={`btn ${filter === 'pending' ? 'btn-warning' : 'btn-outline-warning'}`}
                  onClick={() => setFilter('pending')}
                >
                  Pending ({summary.pendingCount})
                </button>
                <button 
                  className={`btn ${filter === 'approved' ? 'btn-warning' : 'btn-outline-warning'}`}
                  onClick={() => setFilter('approved')}
                >
                  Approved ({summary.approvedCount})
                </button>
                <button 
                  className={`btn ${filter === 'rejected' ? 'btn-warning' : 'btn-outline-warning'}`}
                  onClick={() => setFilter('rejected')}
                >
                  Rejected ({summary.rejectedCount})
                </button>
                <button 
                  className={`btn ${filter === 'cancelled' ? 'btn-warning' : 'btn-outline-warning'}`}
                  onClick={() => setFilter('cancelled')}
                >
                  Cancelled
                </button>
              </div>
            </div>

            {/* Withdrawals Table */}
            {filteredWithdrawals.length === 0 ? (
              <div className="text-center py-5">
                <p style={{ color: '#fff7ca' }}>No withdrawal requests found</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-admin-one">
                  <thead>
                    <tr>
                      <th>Request ID</th>
                      <th>Date</th>
                      <th>Amount</th>
                      <th>Bank Account</th>
                      <th>Status</th>
                      <th>Remarks</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredWithdrawals.map((withdrawal) => (
                      <tr key={withdrawal._id}>
                        <td>
                          <small className="text-muted">{withdrawal.withdrawalId}</small>
                        </td>
                        <td>{formatDate(withdrawal.requestedAt || withdrawal.createdAt)}</td>
                        <td className="fw-bold">{formatAmount(withdrawal.amount)}</td>
                        <td>
                          {withdrawal.bankDetails?.bankName}<br/>
                          <small className="text-muted">
                            Acc: XXXX{withdrawal.bankDetails?.accountNumber?.slice(-4)}<br/>
                            IFSC: {withdrawal.bankDetails?.ifscCode}
                          </small>
                        </td>
                        <td>{getStatusBadge(withdrawal.status)}</td>
                        <td>
                          {withdrawal.remarks || '-'}
                          {withdrawal.processedAt && withdrawal.status !== 'pending' && (
                            <div>
                              <small className="text-muted">
                                Processed: {formatDate(withdrawal.processedAt)}
                              </small>
                            </div>
                          )}
                        </td>
                        <td>
                          {withdrawal.status === 'pending' && (
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => handleCancelWithdrawal(withdrawal._id)}
                            >
                              Cancel
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {pagination.total > pagination.limit && (
              <div className="admin-table-bottom mt-3">
                <div className="table-info">
                  Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
                  {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                  {pagination.total} entries
                </div>
                <div className="table-pagination">
                  <button
                    className="page-btn"
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    disabled={pagination.page === 1}
                  >
                    Previous
                  </button>
                  <span className="page-btn active">
                    Page {pagination.page} of {pagination.pages}
                  </span>
                  <button
                    className="page-btn"
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    disabled={pagination.page === pagination.pages}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WithdrawalHistory;